import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip,
} from '@mui/material';
import { deltaToColor, deltaToBgColor } from './sectorColors';

// ── Mini barra orizzontale con valore ────────────────────────────
function MiniBar({ value, maxValue = 100, color, unit = '', decimals = 0 }) {
  if (value == null) return <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>—</Typography>;
  const pct = Math.min(100, (Math.abs(value) / (maxValue || 1)) * 100);
  const display = typeof value === 'number' ? value.toFixed(decimals) : value;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
      <Box sx={{ width: 44, height: 3, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 1, transition: 'width 0.2s' }} />
      </Box>
      <Typography variant="caption" sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums', lineHeight: 1, color: 'text.primary' }}>
        {display}{unit}
      </Typography>
    </Box>
  );
}

// ── Cella canale generica ────────────────────────────────────────
function ChannelCell({ value, maxValue, color, unit, decimals, label }) {
  if (value == null) {
    return (
      <TableCell align="center" sx={{ px: 1 }}>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>—</Typography>
      </TableCell>
    );
  }
  return (
    <TableCell align="center" sx={{ px: 1 }}>
      <Tooltip title={`${label}: ${value?.toFixed?.(decimals) ?? value}${unit}`} placement="top">
        <Box sx={{ display: 'inline-flex' }}>
          <MiniBar value={value} maxValue={maxValue} color={color} unit={unit} decimals={decimals} />
        </Box>
      </Tooltip>
    </TableCell>
  );
}

// ── Componente principale ────────────────────────────────────────
export default function SectorTable({ sectors = [], activeSector, onRowClick }) {
  const { t } = useTranslation('race_engineer');
  // Tutti i calcoli/hook prima di qualsiasi return condizionale
  const hasRefDelta = sectors.some(s => s.delta_vs_ref_s != null);
  const activeDelta = s => hasRefDelta ? (s.delta_vs_ref_s ?? s.delta_vs_best_s) : s.delta_vs_best_s;
  const totalDelta  = sectors.reduce((acc, s) => acc + activeDelta(s), 0);
  const maxDelta    = Math.max(...sectors.map(s => Math.abs(activeDelta(s))), 0.01);

  const hasBrakePct = sectors.some(s => s.canali?.brake_max_pct           != null);
  const hasBrakeBar = sectors.some(s => s.canali?.brake_pressure_max_bar  != null);
  const hasThrottle = sectors.some(s => s.canali?.throttle_medio_pct      != null);
  const hasLatG     = sectors.some(s => s.canali?.lat_acc_max_g           != null);
  const hasRpm      = sectors.some(s => s.canali?.rpm_medio               != null);
  const hasGear     = sectors.some(s => s.canali?.marcia_apice            != null);
  const hasVmax     = sectors.some(s => s.speed_max_kmh                   != null);
  const hasSteering = sectors.some(s => s.canali?.steering_max_deg        != null);
  const hasLambda   = sectors.some(s => s.canali?.lambda_mean             != null);
  const hasWaterT   = sectors.some(s => s.canali?.water_temp_c            != null);

  const maxThrottle = 100;
  const maxBrakePct = 100;
  const maxBrakeBar = useMemo(() => Math.max(...sectors.map(s => s.canali?.brake_pressure_max_bar ?? 0), 1), [sectors]);
  const maxLatG     = useMemo(() => Math.max(...sectors.map(s => s.canali?.lat_acc_max_g          ?? 0), 1), [sectors]);
  const maxRpm      = useMemo(() => Math.max(...sectors.map(s => s.canali?.rpm_medio              ?? 0), 1), [sectors]);
  const maxSteering = useMemo(() => Math.max(...sectors.map(s => s.canali?.steering_max_deg       ?? 0), 1), [sectors]);
  const maxLambda   = useMemo(() => Math.max(...sectors.map(s => s.canali?.lambda_mean            ?? 0), 1.5), [sectors]);
  const maxWaterT   = useMemo(() => Math.max(...sectors.map(s => s.canali?.water_temp_c           ?? 0), 130), [sectors]);

  if (!sectors.length) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', justifyContent: 'space-between', alignItems: 'center', '@media print': { bgcolor: 'white' } }}>
        <Typography variant="subtitle1" fontWeight={700}>{t('sector_table.title')}</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" display="block">{t('sector_table.total_gap')}</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: totalDelta > 0 ? '#F44336' : '#4CAF50' }}>
            {totalDelta >= 0 ? '+' : ''}{totalDelta.toFixed(3)}s
          </Typography>
        </Box>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', px: 1 } }}>
              <TableCell sx={{ width: 44, pl: 2 }}>S</TableCell>
              <TableCell align="right" sx={{ width: 68 }}>{t('sector_table.time')}</TableCell>
              <TableCell align="right" sx={{ width: 76 }}>{hasRefDelta ? t('sector_table.vs_pole') : t('sector_table.delta')}</TableCell>

              {hasThrottle  && <TableCell align="center" sx={{ width: 62 }}>{t('sector_table.gas')}</TableCell>}
              {hasBrakePct  && <TableCell align="center" sx={{ width: 62 }}>{t('sector_table.brake_pct')}</TableCell>}
              {hasBrakeBar  && <TableCell align="center" sx={{ width: 68 }}>{t('sector_table.brake_pressure')}</TableCell>}
              {hasLatG      && <TableCell align="center" sx={{ width: 58 }}>{t('sector_table.lat_g')}</TableCell>}

              <TableCell align="center" sx={{ width: 60 }}>{t('sector_table.speed_apex')}</TableCell>
              {hasVmax      && <TableCell align="center" sx={{ width: 60 }}>{t('sector_table.speed_exit')}</TableCell>}
              {hasGear      && <TableCell align="center" sx={{ width: 38 }}>{t('sector_table.gear')}</TableCell>}
              {hasRpm       && <TableCell align="center" sx={{ width: 58 }}>{t('sector_table.rpm')}</TableCell>}
              {hasSteering  && <TableCell align="center" sx={{ width: 62 }}>{t('sector_table.steering')}</TableCell>}
              {hasLambda    && <TableCell align="center" sx={{ width: 52 }}>λ</TableCell>}
              {hasWaterT    && <TableCell align="center" sx={{ width: 52 }}>H₂O</TableCell>}

              <TableCell>{t('sector_table.delta_bar')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sectors.map(s => {
              const isActive = s.sector === activeSector;
              const delta    = activeDelta(s);
              const color    = deltaToColor(Math.max(0, delta));
              const bg       = isActive ? deltaToBgColor(delta) : 'transparent';
              const barW     = Math.abs(delta) / maxDelta * 100;
              const sign     = delta >= 0 ? '+' : '';
              const ch       = s.canali || {};

              return (
                <TableRow key={s.sector} hover selected={isActive}
                  onClick={() => onRowClick?.(s.sector)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: bg,
                    '&:hover': { backgroundColor: deltaToBgColor(activeDelta(s)) },
                    '& td': { borderBottom: '1px solid rgba(255,255,255,0.04)', py: 0.75, px: 1 },
                    transition: 'background-color 0.15s',
                  }}
                >
                  {/* Numero settore */}
                  <TableCell sx={{ pl: 2 }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#0A0A0A', lineHeight: 1 }}>
                        {s.sector}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Tempo */}
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                      {s.duration_s.toFixed(3)}s
                    </Typography>
                  </TableCell>

                  {/* Delta */}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} sx={{ color, fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                      {sign}{delta.toFixed(3)}s
                    </Typography>
                    {hasRefDelta && s.ref_duration_s && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, display: 'block', textAlign: 'right' }}>
                        rif: {s.ref_duration_s.toFixed(3)}s
                      </Typography>
                    )}
                  </TableCell>

                  {/* Gas medio */}
                  {hasThrottle && (
                    <ChannelCell value={ch.throttle_medio_pct} maxValue={maxThrottle}
                      color="#81C784" unit="%" decimals={0} label="Gas medio" />
                  )}

                  {/* Freno max % */}
                  {hasBrakePct && (
                    <ChannelCell value={ch.brake_max_pct} maxValue={maxBrakePct}
                      color="#FF7043" unit="%" decimals={0} label="Freno max" />
                  )}

                  {/* Pressione freno max (bar) */}
                  {hasBrakeBar && (
                    <ChannelCell value={ch.brake_pressure_max_bar} maxValue={maxBrakeBar}
                      color="#EF5350" unit="b" decimals={0} label="Pressione freno max" />
                  )}

                  {/* Lat G max */}
                  {hasLatG && (
                    <ChannelCell value={ch.lat_acc_max_g} maxValue={maxLatG}
                      color="#CE93D8" unit="g" decimals={2} label="Lat G max" />
                  )}

                  {/* Velocità apice */}
                  <TableCell align="center">
                    {s.speed_min_kmh != null
                      ? <Typography variant="caption" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{s.speed_min_kmh}<Typography component="span" sx={{ fontSize: 9, color: 'text.disabled', ml: 0.25 }}>km/h</Typography></Typography>
                      : <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>—</Typography>
                    }
                  </TableCell>

                  {/* Velocità uscita */}
                  {hasVmax && (
                    <TableCell align="center">
                      {s.speed_max_kmh != null
                        ? <Typography variant="caption" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{s.speed_max_kmh}<Typography component="span" sx={{ fontSize: 9, color: 'text.disabled', ml: 0.25 }}>km/h</Typography></Typography>
                        : <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>—</Typography>
                      }
                    </TableCell>
                  )}

                  {/* Marcia apice */}
                  {hasGear && (
                    <TableCell align="center">
                      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                        {ch.marcia_apice ?? '—'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* RPM medio */}
                  {hasRpm && (
                    <ChannelCell value={ch.rpm_medio} maxValue={maxRpm}
                      color="#FFB74D" unit="" decimals={0} label="RPM medio" />
                  )}

                  {/* Sterzo max */}
                  {hasSteering && (
                    <ChannelCell value={ch.steering_max_deg} maxValue={maxSteering}
                      color="#29B6F6" unit="°" decimals={0} label="Sterzo max" />
                  )}

                  {/* Lambda medio */}
                  {hasLambda && (
                    <ChannelCell value={ch.lambda_mean} maxValue={maxLambda}
                      color="#FFCC02" unit="λ" decimals={2} label="Lambda" />
                  )}

                  {/* Temperatura acqua */}
                  {hasWaterT && (
                    <ChannelCell value={ch.water_temp_c} maxValue={maxWaterT}
                      color="#26C6DA" unit="°C" decimals={0} label="Acqua" />
                  )}

                  {/* Barra delta visiva */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 20 }}>
                      <Box sx={{ flex: 1, height: 4, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${barW}%`, bgcolor: color, borderRadius: 2, transition: 'width 0.3s ease', opacity: delta >= 0 ? 0.85 : 0.6 }} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
